/**
 * Dashboard Principal
 */

import React, { useState, useEffect, useMemo } from 'react';
import { Row, Col, Select, Typography, Spin, message } from 'antd';
import {
  UserOutlined,
  BookOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import styled from 'styled-components';
import { Pie, Column } from '@ant-design/plots';
import {
  getAllWidgets,
  normalizeDashboardData,
  getDashboardChart,
  normalizeDashboardChartData,
} from '../../services/dashboard';
import { MONTHS_OPTIONS } from '../../utils/constants';

const { Title, Text } = Typography;

const COLORS = {
  primary: '#7C3AED',
  primaryLight: '#A78BFA',
  primaryLighter: '#C4B5FD',
  accent: '#8B5CF6',
  gray: '#9CA3AF',
  cardBg: '#FFFFFF',
  pageBg: '#F3F4F6',
  textMain: '#111827',
  textMuted: '#6B7280',
  incomePillBg: '#EDE9FE',
  border: '#E5E7EB',
};

const PERIOD_OPTIONS = [
  { value: 'current_month', label: 'Mes actual' },
  { value: 'last_3_months', label: 'Ultimos 3 meses' },
  { value: 'last_6_months', label: 'Ultimos 6 meses' },
  { value: 'last_12_months', label: 'Ultimos 12 meses' },
  { value: 'last_3_years', label: 'Ultimos 3 anos' },
  { value: 'last_5_years', label: 'Ultimos 5 anos' },
];

const CATEGORY_OPTIONS = [
  { value: 'users_new', label: 'Usuarios nuevos' },
  { value: 'users_active', label: 'Usuarios activos' },
  { value: 'users_inactive', label: 'Usuarios no activos' },
  { value: 'subscriptions_new', label: 'Suscripciones nuevas' },
  { value: 'subscriptions_not_renewed', label: 'Suscripciones no renovadas' },
  { value: 'revenue', label: 'Ingresos' },
];

const TREND_BAR_COLORS = [
  '#5B21B6',
  '#6D28D9',
  '#7C3AED',
  '#8B5CF6',
  '#A78BFA',
  '#C4B5FD',
  '#DDD6FE',
  '#EDE9FE',
  '#5B21B6',
  '#6D28D9',
  '#7C3AED',
];

const EMPTY_TREND_DATA = {
  period: 'last_12_months',
  category: 'users_new',
  categoryLabel: 'Usuarios nuevos',
  granularity: 'month',
  total: 0,
  max: 0,
  min: 0,
  anchor: {
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
  },
  series: [],
};

// Contenedor principal del dashboard
const DashboardContainer = styled.div`
  padding: 20px;
  background-color: ${COLORS.pageBg};
  min-height: calc(100vh - 64px);
`;

// Seccion del filtro de fecha
const FilterSection = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 20px;
  gap: 12px;
  flex-wrap: wrap;
`;

const RoundedSelect = styled(Select)`
  .ant-select-selector {
    border-radius: 21px !important;
    min-height: 42px !important;
    display: flex !important;
    align-items: center !important;
    padding-left: 14px !important;
  }
`;

const WidgetCard = styled.div`
  background-color: ${COLORS.cardBg};
  border: 1px solid ${COLORS.border};
  border-radius: 24px;
  padding: 20px;
  color: ${COLORS.textMain};
  height: 100%;
  position: relative;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
`;

// Contenedor del encabezado
const WidgetHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 20px;
`;

// Icono circular en la esquina superior
const IconCircle = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background-color: ${COLORS.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  .anticon {
    font-size: 30px;
    color: white;
  }
`;

// Titulo del widget
const WidgetTitle = styled(Title)`
  color: ${COLORS.textMain} !important;
  margin: 0 !important;
  font-weight: 600 !important;
`;

// Contenedor de dos columnas para KPIs y grafica
const TwoColumnLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  align-items: center;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

// Contenedor de KPIs en vertical
const KPIColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

// Item de KPI individual
const KPIItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const KPILabel = styled(Text)`
  font-size: 12px;
  display: block;
  font-weight: 500;
`;

const KPIValue = styled(Text)`
  color: ${COLORS.textMain};
  font-size: 40px;
  font-weight: bold;
  display: block;
  line-height: 1;
`;

// Contenedor de grafica
const ChartContainer = styled.div`
  height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

// Pildora de ingresos
const IncomePill = styled.div`
  background: ${COLORS.incomePillBg};
  border-radius: 20px;
  padding: 12px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 16px;
`;

const IncomePillLabel = styled(Text)`
  color: ${COLORS.textMuted};
  font-size: 14px;
`;

const IncomePillValue = styled(Text)`
  color: ${COLORS.primary};
  font-size: 18px;
  font-weight: bold;
`;

// Lista de rutinas favoritas
const RoutineList = styled.div`
  margin-top: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const RoutineItem = styled.div`
  color: ${COLORS.textMain};
  font-size: 14px;
  font-weight: 600;
`;

const BottomFilterSection = styled.div`
  display: flex;
  align-items: center;
  gap: 26px;
  margin-top: 28px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`;

const BottomFilterGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const BottomChartCard = styled.div`
  background-color: ${COLORS.cardBg};
  border: 1px solid ${COLORS.border};
  border-radius: 24px;
  padding: 20px;
  color: ${COLORS.textMain};
  min-height: 420px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
`;

const BottomChartContainer = styled.div`
  height: 340px;
`;

const BottomLegend = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px 18px;
  margin-top: 12px;
  padding: 0 4px;
`;

const BottomLegendItem = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

const BottomLegendDot = styled.span`
  width: 10px;
  height: 10px;
  border-radius: 999px;
  background-color: ${({ $color }) => $color};
  flex-shrink: 0;
`;

const BottomEmptyState = styled.div`
  min-height: 320px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${COLORS.textMuted};
  font-size: 16px;
  border: 2px dashed ${COLORS.primaryLight};
  border-radius: 16px;
  background: ${COLORS.cardBg};
  padding: 24px;
`;

const Dashboard = () => {
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  const [selectedPeriod, setSelectedPeriod] = useState('last_12_months');
  const [selectedCategory, setSelectedCategory] = useState('users_new');

  const [widgetsLoading, setWidgetsLoading] = useState(false);
  const [trendLoading, setTrendLoading] = useState(false);

  const [dashboardData, setDashboardData] = useState({
    users: {
      active: 0,
      newThisMonth: 0,
      inactive: 0
    },
    subscriptions: {
      newThisMonth: 0,
      revenueThisMonth: 0,
      notRenewed: 0
    },
    favoriteRoutines: []
  });

  const [dashboardTrend, setDashboardTrend] = useState(EMPTY_TREND_DATA);

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: currentYear - 2020 + 3 }, (_, i) => 2020 + i);

  const loadDashboardData = async () => {
    setWidgetsLoading(true);
    try {
      const data = await getAllWidgets(selectedMonth, selectedYear);
      const normalized = normalizeDashboardData(data);
      setDashboardData(normalized);
    } catch (error) {
      message.error('Error al cargar los datos del dashboard');
      console.error('Error:', error);
    } finally {
      setWidgetsLoading(false);
    }
  };

  const loadDashboardTrend = async () => {
    setTrendLoading(true);
    try {
      const data = await getDashboardChart({
        period: selectedPeriod,
        category: selectedCategory,
        month: selectedMonth,
        year: selectedYear,
      });
      const normalized = normalizeDashboardChartData(data);
      setDashboardTrend(normalized);
    } catch (error) {
      message.error('Error al cargar la grafica del dashboard');
      console.error('Error:', error);
    } finally {
      setTrendLoading(false);
    }
  };

  useEffect(() => {
    loadDashboardData();
  }, [selectedMonth, selectedYear]);

  useEffect(() => {
    loadDashboardTrend();
  }, [selectedPeriod, selectedCategory]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN'
    }).format(value);
  };

  const formatCompactNumber = (value) => {
    return new Intl.NumberFormat('es-MX', {
      maximumFractionDigits: 2,
    }).format(value);
  };

  // Datos para grafica de dona (Usuarios)
  const pieData = [
    { type: 'Activos', value: dashboardData.users.active },
    { type: 'Nuevos', value: dashboardData.users.newThisMonth },
    { type: 'Inactivos', value: dashboardData.users.inactive }
  ];

  const pieConfig = {
    data: pieData,
    angleField: 'value',
    colorField: 'type',
    scale: {
      color: {
        domain: ['Activos', 'Nuevos', 'Inactivos'],
        range: [COLORS.primary, COLORS.primaryLight, COLORS.gray],
      },
    },
    innerRadius: 0.42,
    legend: false,
    tooltip: false,
    label: {
      text: (datum) => `${datum.value ?? 0}`,
      position: 'inside',
      style: {
        fill: '#fff',
        fontSize: 14,
        fontWeight: 'bold',
      },
    },
    statistic: {
      title: false,
      content: false
    }
  };

  // Datos para grafica de barras (Suscripciones)
  const subscriptionColumnData = [
    { category: 'Nuevas', value: dashboardData.subscriptions.newThisMonth },
    { category: 'No renovado', value: dashboardData.subscriptions.notRenewed }
  ];

  const subscriptionColumnConfig = {
    data: subscriptionColumnData,
    xField: 'category',
    yField: 'value',
    colorField: 'category',
    scale: {
      color: {
        domain: ['Nuevas', 'No renovado'],
        range: [COLORS.primary, COLORS.primaryLight],
      },
      y: {
        nice: true,
        domainMin: 0,
      },
    },
    axis: {
      x: {
        title: false,
        labelFill: COLORS.textMuted,
        labelFontSize: 13,
        labelFontWeight: 500,
        tick: false,
        line: false,
      },
      y: {
        title: false,
        labelFill: COLORS.textMuted,
        labelFontSize: 11,
        labelFontWeight: 500,
        tick: false,
        line: false,
        grid: true,
        gridStroke: COLORS.border,
        gridLineWidth: 1,
      },
    },
    style: {
      radiusTopLeft: 4,
      radiusTopRight: 4,
    },
    legend: false,
    tooltip: false,
  };

  const trendChartData = useMemo(() => {
    return dashboardTrend.series.map((point, index) => ({
      ...point,
      colorKey: point.key ?? `item-${index + 1}`,
      barColor: TREND_BAR_COLORS[index % TREND_BAR_COLORS.length],
    }));
  }, [dashboardTrend.series]);

  const trendColorDomain = trendChartData.map((item) => item.colorKey);
  const trendColorRange = trendChartData.map((item) => item.barColor);

  const trendColumnConfig = {
    data: trendChartData,
    xField: 'shortLabel',
    yField: 'value',
    colorField: 'colorKey',
    scale: {
      color: {
        domain: trendColorDomain.length > 0 ? trendColorDomain : ['default'],
        range: trendColorRange.length > 0 ? trendColorRange : [COLORS.primary],
      },
      y: {
        nice: true,
        domainMin: 0,
      },
    },
    axis: {
      x: {
        title: false,
        labelFill: COLORS.textMuted,
        labelFontSize: 11,
        labelFontWeight: 500,
        tick: false,
        line: false,
      },
      y: {
        title: false,
        labelFill: COLORS.textMuted,
        labelFontSize: 12,
        labelFontWeight: 500,
        tick: false,
        line: false,
        grid: true,
        gridStroke: COLORS.border,
        gridLineWidth: 1,
      },
    },
    label: {
      text: (result) => (result.value === 0 ? '' : String(result.value)),
      position: 'top',
      style: {
        fill: COLORS.textMain,
        fontSize: 11,
        fontWeight: 600,
      },
      offset: 6,
    },
    style: {
      radiusTopLeft: 3,
      radiusTopRight: 3,
    },
    legend: false,
    tooltip: false,
    padding: [20, 12, 40, 10],
  };

  return (
    <DashboardContainer>
      {/* Filtro de fecha */}
      <FilterSection>
        <Title level={5} style={{ margin: 0, color: COLORS.textMain }}>Resultados de:</Title>
        <RoundedSelect
          value={selectedMonth}
          onChange={setSelectedMonth}
          style={{ width: 180 }}
          disabled={widgetsLoading}
          options={MONTHS_OPTIONS}
        />
        <RoundedSelect
          value={selectedYear}
          onChange={setSelectedYear}
          style={{ width: 130 }}
          disabled={widgetsLoading}
          options={years.map((year) => ({ value: year, label: year }))}
        />
      </FilterSection>

      {/* Widgets */}
      <Spin spinning={widgetsLoading}>
        <Row gutter={[20, 20]}>
          {/* Widget de Usuarios */}
          <Col xs={24} md={12} lg={8}>
            <WidgetCard>
              <WidgetHeader>
                <IconCircle>
                  <UserOutlined />
                </IconCircle>
                <WidgetTitle level={3}>Usuarios</WidgetTitle>
              </WidgetHeader>
              <TwoColumnLayout>
                <KPIColumn>
                  <KPIItem>
                    <KPILabel style={{ color: COLORS.primary }}>Activos</KPILabel>
                    <KPIValue>{dashboardData.users.active}</KPIValue>
                  </KPIItem>
                  <KPIItem>
                    <KPILabel style={{ color: COLORS.primaryLight }}>Nuevos del mes</KPILabel>
                    <KPIValue>{dashboardData.users.newThisMonth}</KPIValue>
                  </KPIItem>
                  <KPIItem>
                    <KPILabel style={{ color: COLORS.gray }}>No activos</KPILabel>
                    <KPIValue>{dashboardData.users.inactive}</KPIValue>
                  </KPIItem>
                </KPIColumn>
                <ChartContainer>
                  <Pie {...pieConfig} />
                </ChartContainer>
              </TwoColumnLayout>
            </WidgetCard>
          </Col>

          {/* Widget de Suscripciones */}
          <Col xs={24} md={12} lg={8}>
            <WidgetCard>
              <WidgetHeader>
                <IconCircle>
                  <BookOutlined />
                </IconCircle>
                <WidgetTitle level={3}>Suscripciones</WidgetTitle>
              </WidgetHeader>
              <TwoColumnLayout>
                <KPIColumn>
                  <KPIItem>
                    <KPILabel style={{ color: COLORS.primary }}>Nuevas</KPILabel>
                    <KPIValue>{dashboardData.subscriptions.newThisMonth}</KPIValue>
                  </KPIItem>
                  <KPIItem>
                    <KPILabel style={{ color: COLORS.primaryLight }}>No renovado</KPILabel>
                    <KPIValue>{dashboardData.subscriptions.notRenewed}</KPIValue>
                  </KPIItem>
                </KPIColumn>
                <ChartContainer>
                  <Column {...subscriptionColumnConfig} />
                </ChartContainer>
              </TwoColumnLayout>
              <IncomePill>
                <IncomePillLabel>Ingresos del mes</IncomePillLabel>
                <IncomePillValue>
                  {formatCurrency(dashboardData.subscriptions.revenueThisMonth)} MXN
                </IncomePillValue>
              </IncomePill>
            </WidgetCard>
          </Col>

          {/* Widget de Rutinas */}
          <Col xs={24} md={24} lg={8}>
            <WidgetCard>
              <WidgetHeader>
                <IconCircle>
                  <ThunderboltOutlined />
                </IconCircle>
                <WidgetTitle level={3}>Rutinas</WidgetTitle>
              </WidgetHeader>
              <Title level={5} style={{ color: COLORS.textMain, marginTop: 0, marginBottom: 8 }}>
                Favoritas
              </Title>
              <RoutineList>
                {dashboardData.favoriteRoutines.length > 0 ? (
                  dashboardData.favoriteRoutines.map((routine, index) => (
                    <RoutineItem key={routine.id || index}>
                      {index + 1}- {routine.title} - {routine.location}
                    </RoutineItem>
                  ))
                ) : (
                  <Text style={{ color: COLORS.textMuted }}>
                    No hay rutinas favoritas este mes
                  </Text>
                )}
              </RoutineList>
            </WidgetCard>
          </Col>
        </Row>
      </Spin>

      {/* Filtros de grafica inferior */}
      <BottomFilterSection>
        <BottomFilterGroup>
          <Title level={3} style={{ margin: 0, color: COLORS.textMain }}>Periodo:</Title>
          <RoundedSelect
            value={selectedPeriod}
            onChange={setSelectedPeriod}
            style={{ width: 290 }}
            disabled={trendLoading}
            options={PERIOD_OPTIONS}
          />
        </BottomFilterGroup>

        <BottomFilterGroup>
          <Title level={3} style={{ margin: 0, color: COLORS.textMain }}>Categoria:</Title>
          <RoundedSelect
            value={selectedCategory}
            onChange={setSelectedCategory}
            style={{ width: 310 }}
            disabled={trendLoading}
            options={CATEGORY_OPTIONS}
          />
        </BottomFilterGroup>
      </BottomFilterSection>

      {/* Grafica inferior */}
      <BottomChartCard>
        <Spin spinning={trendLoading}>
          {trendChartData.length > 0 ? (
            <>
              <BottomChartContainer>
                <Column {...trendColumnConfig} />
              </BottomChartContainer>

              <BottomLegend>
                {trendChartData.map((item) => (
                  <BottomLegendItem key={`legend-${item.colorKey}`}>
                    <BottomLegendDot $color={item.barColor} />
                    <Text style={{ color: COLORS.textMuted, margin: 0 }}>
                      {item.label} ({formatCompactNumber(item.value)})
                    </Text>
                  </BottomLegendItem>
                ))}
              </BottomLegend>
            </>
          ) : (
            <BottomEmptyState>
              No hay datos para el periodo y categoria seleccionados.
            </BottomEmptyState>
          )}
        </Spin>
      </BottomChartCard>
    </DashboardContainer>
  );
};

export default Dashboard;